import React, { useMemo, useContext, createContext, useCallback, useRef } from 'react';
import { HTTPError } from 'clients/HTTPClient';
import unauthorized from '../unauthorized';
import useReducerWithMiddleware from '../useReducerWithMiddleware';
import {
    ADD_DISCOUNT_CODE_REQUESTED,
    ADD_DISCOUNT_CODE_SUCCESS,
    ADD_DISCOUNT_CODE_FAILED,
    DISCOUNT_CODES_REQUESTED,
    DISCOUNT_CODES_SUCCESS,
    DISCOUNT_CODES_FAILED,
    DELETE_DISCOUNT_CODE_REQUESTED,
    DELETE_DISCOUNT_CODE_SUCCESS,
    DELETE_DISCOUNT_CODE_FAILED,
} from './constants';
import discountCodeReducer, { DiscountCodeState, initialDiscountCodeState } from './reducer';
import { discountCodeClient } from 'clients';
import { ApplicableRentalTypeOptions } from 'components/Dashboard/DiscountCodeModalForm/utils';
import { PromoCodeSortColumn, Sort } from 'types';

type DiscountCodeFilter = {
    page: number;
    usableCodes: boolean;
    sort?: Sort<PromoCodeSortColumn> | null;
    search?: string;
};

export type DiscountCodeFormInputs = {
    code: string;
    notes: string;
    value: string | null;
    expiration: Date | null;
    valid_from: Date | null;
    number_of_uses: string | null;
    applicable_rental_types: ApplicableRentalTypeOptions[];
    percentage_discount: string | null;
    minutes_to_discount: number | null;
    hours_to_discount: number | null;
    maximum_number_of_simultaneous_rentals: number | null;
};

interface DiscountCodeActions {
    getDiscountCodes: (spotPk: number, filters: DiscountCodeFilter, force?: boolean) => Promise<void>;
    addDiscountCode: (
        spotPk: number,
        discountCode: DiscountCodeFormInputs,
        filters: DiscountCodeFilter
    ) => Promise<void>;
    deleteDiscountCode: (spotPk: number, discountCodePk: number, filters: DiscountCodeFilter) => Promise<void>;
}

interface DiscountCodeContextType extends DiscountCodeState, DiscountCodeActions {}

export const DiscountCodeContext = createContext<DiscountCodeContextType | null>(null);

export const DiscountCodeProvider: React.FunctionComponent<React.PropsWithChildren<object>> = ({ children }) => {
    const [state, dispatch] = useReducerWithMiddleware(
        discountCodeReducer,
        { ...initialDiscountCodeState },
        [],
        [unauthorized]
    );

    // Filters can change while a fetch is in flight; only the latest request may update the table.
    const latestRequestId = useRef(0);

    const getDiscountCodes = useCallback(
        async (spotPk: number, { page, usableCodes, sort, search }: DiscountCodeFilter, force: boolean = false) => {
            const requestId = ++latestRequestId.current;
            dispatch({ type: DISCOUNT_CODES_REQUESTED });

            try {
                const payload = {
                    spot: spotPk,
                    page,
                    usableCodes,
                    sort,
                    search,
                };

                let discountCode;
                if (force) discountCode = await discountCodeClient.force__getDiscountCodes(payload);
                else discountCode = await discountCodeClient.getDiscountCodes(payload);

                if (requestId !== latestRequestId.current) return;
                dispatch({ type: DISCOUNT_CODES_SUCCESS, payload: { discountCode } });
            } catch (error) {
                if (requestId !== latestRequestId.current) return;
                dispatch({
                    type: DISCOUNT_CODES_FAILED,
                    payload: { error, message: error?.toString() || 'Failed to fetch discount codes' },
                });
            }
        },
        // until this is fixed: https://github.com/reactjs/react.dev/issues/1889,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const addDiscountCode = useCallback(
        async (spotPk: number, discountCode: DiscountCodeFormInputs, filters: DiscountCodeFilter) => {
            dispatch({ type: ADD_DISCOUNT_CODE_REQUESTED });
            try {
                let adjustedValue: number | null = null;
                if (typeof discountCode.value === 'string' && discountCode.value !== '') {
                    // This happens only for dollar based discount codes.
                    adjustedValue = parseInt(discountCode.value) * 100;
                }

                await discountCodeClient.addDiscountCode({
                    ...discountCode,
                    value: adjustedValue,
                    spot: spotPk,
                });

                dispatch({ type: ADD_DISCOUNT_CODE_SUCCESS });
                discountCodeClient.clearDiscountCodesCache();
                getDiscountCodes(spotPk, filters, true);
            } catch (error) {
                let errorMessage;
                if (HTTPError.isHTTPError(error)) {
                    errorMessage = error.message;
                } else {
                    errorMessage = error?.toString() || 'Failed to add a discount code';
                }
                dispatch({ type: ADD_DISCOUNT_CODE_FAILED, payload: { message: errorMessage } });
            }
        },
        // until this is fixed: https://github.com/reactjs/react.dev/issues/1889,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const deleteDiscountCode = useCallback(
        async (spotPk: number, discountCodePk: number, filters: DiscountCodeFilter) => {
            dispatch({ type: DELETE_DISCOUNT_CODE_REQUESTED });
            try {
                await discountCodeClient.deleteDiscountCode(discountCodePk);

                dispatch({ type: DELETE_DISCOUNT_CODE_SUCCESS });
                discountCodeClient.clearDiscountCodesCache();
                getDiscountCodes(spotPk, filters, true);
            } catch (e) {
                let errorMessage;
                if (HTTPError.isHTTPError(e)) {
                    errorMessage = e.message;
                } else {
                    errorMessage = e?.toString() || 'Could not delete discount code';
                }
                dispatch({ type: DELETE_DISCOUNT_CODE_FAILED, payload: { message: errorMessage } });
                return;
            }
        },
        // until this is fixed: https://github.com/reactjs/react.dev/issues/1889,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const value = useMemo<DiscountCodeContextType>(() => {
        return {
            ...state,
            getDiscountCodes,
            addDiscountCode,
            deleteDiscountCode,
        };
    }, [state, getDiscountCodes, addDiscountCode, deleteDiscountCode]);
    return <DiscountCodeContext.Provider value={value}>{children}</DiscountCodeContext.Provider>;
};

export const useDiscountCode = () => {
    const context = useContext(DiscountCodeContext);
    if (!context) {
        throw new Error('Error: useDiscountCode should be wrapped by DiscountCodeProvider.');
    }
    return context;
};
